import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, LockKey, EnvelopeSimple, Shield, Package, Truck } from "@phosphor-icons/react";
import { toast } from "sonner";
import StonLogo from "@/components/StonLogo";

const BG_VIDEO_URL =
  "https://customer-assets-gfyr7b9c.emergentagent.net/job_49da239b-17c3-446b-ad3f-8cc787e97c0e/artifacts/r1dz08k1_12336972-hd_1920_1028_60fps.mp4";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const navigate = useNavigate();
  const { setUser, enterGuest } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const formatDetail = (detail) => {
    if (!detail) return "Login failed. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
    return String(detail);
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // STON LINK-owned Google OAuth. Relative same-origin URL so /api hits our backend,
    // which derives the redirect_uri from the current origin (www.stonlink.com in prod).
    window.location.href = "/api/auth/google/login";
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError("Enter your email and password."); return; }
    setSubmitting(true);
    setError("");
    try {
      const res = await api.post("/auth/login", { email: email.trim(), password });
      setUser(res.data.user);
      toast.success(`Welcome, ${res.data.user.name}`);
      navigate(res.data.user.role === "admin" ? "/admin" : "/home");
    } catch (err) {
      console.error("[Login] failed:", err);
      setError(formatDetail(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen relative overflow-hidden text-white"
      data-testid="login-page"
    >
      {/* Full-bleed background video */}
      <video
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        src={BG_VIDEO_URL}
        autoPlay
        loop
        muted
        playsInline
        data-testid="login-bg-video"
      />
      {/* Dark overlay for text legibility */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/60 to-black/45 pointer-events-none" />

      <div className="relative min-h-screen grid grid-cols-1 lg:grid-cols-2">
        {/* Left: brand / hero */}
        <div className="hidden lg:flex flex-col justify-between p-12 xl:p-16">
          <div className="flex items-center" />

          <div className="max-w-lg">
            <div className="mt-tiny text-white/80 mb-6">Curated · Certified · Delivered</div>
            <h1 className="font-display text-5xl xl:text-6xl font-black leading-[1.02] tracking-tight text-white">
              Every finish,<br />sourced with intent.
            </h1>
            <p className="mt-6 text-white/80 text-lg leading-relaxed max-w-md">
              From marble slabs to hardwood veneers — track every material through quality, certification, and delivery. All from one place.
            </p>

            <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
              {[
                { icon: Package, label: "Live order status" },
                { icon: Shield, label: "QA certificates" },
                { icon: Truck, label: "Shipment tracking" },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="border-l-2 border-white/30 pl-3">
                  <Icon size={24} weight="duotone" className="text-white" />
                  <div className="mt-2 text-sm text-white/80">{label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-tiny text-white/70">© 2026 Ston Link — Building Materials</div>
        </div>

        {/* Right: login card */}
        <div className="flex items-center justify-center p-6 md:p-12">
          <form
            onSubmit={handleLogin}
            className="w-full max-w-md p-8 md:p-10 bg-white/95 backdrop-blur-md border border-zinc-900/10 rounded-3xl shadow-[0_20px_80px_-20px_rgba(60,40,10,0.35)]"
            data-testid="login-card"
          >
            {/* Mobile brand block */}
            <div className="lg:hidden mb-8">
              <StonLogo size={34} subtitle="Building Materials" data-testid="login-brand-logo-mobile" className="text-zinc-900" />
            </div>

            <div className="mt-tiny text-[#8B4513]">Sign in</div>
            <h2 className="font-display text-3xl md:text-4xl font-black mt-3 tracking-tight text-zinc-900">
              Welcome back.
            </h2>
            <p className="mt-3 text-zinc-600 text-sm">
              Sign in with Google, or use the credentials provided by your Ston Link account manager.
            </p>

            <button
              type="button"
              onClick={handleGoogle}
              data-testid="google-login-button"
              className="mt-6 w-full flex items-center justify-center gap-3 bg-white hover:bg-zinc-50 border border-zinc-300 hover:border-zinc-900 text-zinc-900 px-5 py-3.5 transition-colors duration-200 font-medium"
            >
              <svg width="18" height="18" viewBox="0 0 48 48">
                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.2 6.1 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
                <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.6 18.9 12.8 24 12.8c3 0 5.8 1.1 7.9 3l5.7-5.7C34.2 6.1 29.4 4 24 4 16 4 9.2 8.6 6.3 14.7z"/>
                <path fill="#4CAF50" d="M24 44c5.3 0 10.1-2 13.7-5.3l-6.3-5.2c-2 1.4-4.6 2.3-7.4 2.3-5.3 0-9.7-3.4-11.3-8l-6.5 5C9 39.4 15.9 44 24 44z"/>
                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.2 5.5l6.3 5.2C41 34.9 44 30 44 24c0-1.3-.1-2.4-.4-3.5z"/>
              </svg>
              Continue with Google
            </button>

            <div className="my-6 flex items-center gap-4 text-zinc-400 mt-tiny">
              <div className="h-px flex-1 bg-zinc-200" />
              or use email
              <div className="h-px flex-1 bg-zinc-200" />
            </div>

            {/* Email */}
            <div>
              <label className="mt-tiny text-zinc-500 mb-2 block" htmlFor="login-email">Email</label>
              <div className="relative">
                <EnvelopeSimple size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  id="login-email"
                  type="email"
                  data-testid="login-email-input"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="w-full pl-10 pr-4 py-3 bg-white border border-zinc-300 focus:border-zinc-900 outline-none text-zinc-900 placeholder:text-zinc-400 transition-colors"
                />
              </div>
            </div>

            {/* Password */}
            <div className="mt-5">
              <label className="mt-tiny text-zinc-500 mb-2 block" htmlFor="login-password">Password</label>
              <div className="relative">
                <LockKey size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  id="login-password"
                  type="password"
                  data-testid="login-password-input"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-10 pr-4 py-3 bg-white border border-zinc-300 focus:border-zinc-900 outline-none text-zinc-900 placeholder:text-zinc-400 transition-colors"
                />
              </div>
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="mt-4 border-l-2 border-[#8B4513] bg-[#8B4513]/10 pl-3 py-2 text-sm text-[#5C2E0D]"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-button"
              className="mt-8 w-full flex items-center justify-center gap-3 bg-zinc-900 hover:bg-zinc-800 text-white px-5 py-4 transition-colors duration-200 font-semibold disabled:opacity-60"
            >
              {submitting ? "Signing in..." : (<>Login <ArrowRight size={16} weight="bold" /></>)}
            </button>

            <button
              type="button"
              onClick={() => { enterGuest(); navigate("/home"); }}
              data-testid="guest-browse-button"
              className="mt-4 w-full flex items-center justify-center gap-2 bg-zinc-900 hover:bg-zinc-800 text-white py-3.5 font-medium tracking-tight transition-colors group"
            >
              <Package size={18} weight="fill" className="text-[#FF5A1F]" />
              Browse Catalog as Guest
              <ArrowRight size={16} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
            </button>

            <button
              type="button"
              onClick={() => navigate("/support")}
              data-testid="login-support-link"
              className="mt-5 w-full text-center text-sm text-zinc-500 hover:text-[#8B4513] underline underline-offset-4 decoration-zinc-300 hover:decoration-[#8B4513] transition-colors"
            >
              Support &amp; Contact
            </button>

            <button
              type="button"
              onClick={() => navigate("/privacy-policy")}
              data-testid="login-privacy-link"
              className="mt-2 w-full text-center text-sm text-zinc-500 hover:text-[#8B4513] underline underline-offset-4 decoration-zinc-300 hover:decoration-[#8B4513] transition-colors"
            >
              Privacy Policy
            </button>

            <div className="mt-tiny text-zinc-500 mt-8 text-center">
              Don&apos;t have credentials yet? Contact your Ston Link account manager.
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
